import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const ReportDetails = () => {
    const { targetId, targetType } = useParams();
    const navigate = useNavigate();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDetails();
    }, [targetId, targetType]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await AdminService.getReportDetails(targetId, targetType);
            if (response.data && response.data.status) {
                setDetails(response.data.data);
            } else {
                toast.error("Failed to fetch report details");
            }
        } catch (error) {
            console.error("Error fetching report details:", error);
            toast.error("An error occurred while fetching report details");
        } finally {
            setLoading(false);
        }
    };

    const getModerationBadge = (status) => {
        switch (status) {
            case "DISABLED": return { bg: '#fff3e0', color: '#e65100', label: 'Auto-Disabled' };
            case "BLOCKED": return { bg: '#fce4ec', color: '#c62828', label: 'Blocked' };
            case "PERMANENTLY_BLOCKED": return { bg: '#f3e5f5', color: '#6a1b9a', label: 'Permanently Blocked' };
            case "ACTIVE": return { bg: '#e8f5e9', color: '#2e7d32', label: 'Active' };
            default: return { bg: '#f5f5f5', color: '#757575', label: status || 'Unknown' };
        }
    };

    const getReasonLabel = (code) => {
        const labels = {
            'FAKE': 'Fake Ad / Scam',
            'SPAM': 'Spam',
            'INAPPROPRIATE': 'Inappropriate Content',
            'DUPLICATE': 'Duplicate Listing',
            'OTHER': 'Other'
        };
        return labels[code] || code;
    };

    if (loading) {
        return (
            <div className="admin-container">
                <div className="text-center p-5">
                    <i className="fa fa-spinner fa-spin fa-2x" style={{ color: 'var(--admin-primary)' }}></i>
                    <p style={{ marginTop: '10px', color: '#666' }}>Loading report details...</p>
                </div>
            </div>
        );
    }

    if (!details) {
        return (
            <div className="admin-container">
                <div className="text-center p-5">
                    <i className="fa fa-exclamation-circle fa-3x" style={{ color: '#ccc' }}></i>
                    <p style={{ marginTop: '10px', color: '#666' }}>Report details not found.</p>
                    <button className="admin-btn admin-btn-outline" onClick={() => navigate('/admin/reports')}>
                        <i className="fa fa-arrow-left"></i> Back to Reports
                    </button>
                </div>
            </div>
        );
    }

    const modBadge = getModerationBadge(details.moderationStatus);

    return (
        <div className="admin-container">
            {/* Back Button */}
            <div style={{ marginBottom: '20px' }}>
                <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => navigate('/admin/reports')}>
                    <i className="fa fa-arrow-left"></i> Back to Reports
                </button>
            </div>

            {/* Product Info Card */}
            <div className="admin-card" style={{ marginBottom: '25px' }}>
                <div className="admin-card-body" style={{ padding: '25px' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                        {details.productImageUrl ? (
                            <img
                                src={details.productImageUrl}
                                alt="Product"
                                style={{ width: '80px', height: '80px', borderRadius: '12px', objectFit: 'cover', border: '2px solid #eee' }}
                            />
                        ) : (
                            <div style={{ width: '80px', height: '80px', borderRadius: '12px', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '28px' }}>
                                <i className={`fa ${targetType === 'LISTING' ? 'fa-image' : 'fa-user'}`}></i>
                            </div>
                        )}
                        <div style={{ flex: 1 }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#222' }}>
                                {details.productName || `${targetType} #${targetId}`}
                            </h3>
                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '14px', color: '#666' }}>
                                    <i className="fa fa-user" style={{ marginRight: '5px', color: '#999' }}></i>
                                    Owner: <strong>{details.ownerName || 'Unknown'}</strong>
                                </span>
                                {details.ownerId && (
                                    <button
                                        className="admin-btn admin-btn-outline admin-btn-sm"
                                        onClick={() => navigate(`/admin/reports/user/${details.ownerId}`)}
                                        style={{ fontSize: '12px', borderColor: '#00796b', color: '#00796b' }}
                                    >
                                        <i className="fa fa-external-link"></i> View User's Products
                                    </button>
                                )}
                                <span style={{
                                    background: modBadge.bg, color: modBadge.color,
                                    padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700'
                                }}>
                                    {modBadge.label}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reporting Insights */}
            <div className="admin-kpi-grid" style={{ marginBottom: '25px' }}>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon" style={{ background: 'linear-gradient(135deg, #f44336, #d32f2f)' }}>
                        <i className="fa fa-flag"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4>{details.totalReports}</h4>
                        <p>Total Reports</p>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon" style={{ background: 'linear-gradient(135deg, #2196f3, #1565c0)' }}>
                        <i className="fa fa-users"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4>{details.uniqueReporters}</h4>
                        <p>Unique Reporters</p>
                    </div>
                </div>
                <div className="admin-kpi-card">
                    <div className="admin-kpi-icon" style={{ background: 'linear-gradient(135deg, #ff9800, #f57c00)' }}>
                        <i className="fa fa-exclamation-triangle"></i>
                    </div>
                    <div className="admin-kpi-info">
                        <h4 style={{ fontSize: '16px' }}>{getReasonLabel(details.mostFrequentReason)}</h4>
                        <p>Most Frequent Reason</p>
                    </div>
                </div>
            </div>

            {/* Individual Reports List */}
            <div className="admin-card">
                <div className="admin-card-body" style={{ padding: '0' }}>
                    <div style={{ padding: '20px 20px 10px', borderBottom: '1px solid #eee' }}>
                        <h4 style={{ margin: 0, fontWeight: '700', color: '#333', fontSize: '16px' }}>
                            <i className="fa fa-list" style={{ marginRight: '8px', color: '#999' }}></i>
                            Report History ({details.reports?.length || 0})
                        </h4>
                    </div>
                    <div className="table-responsive">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Reported By</th>
                                    <th>Reason</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th>Action Taken</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.reports && details.reports.length > 0 ? (
                                    details.reports.map((entry, index) => (
                                        <tr key={entry.reportId}>
                                            <td style={{ fontWeight: '600', color: '#999' }}>{index + 1}</td>
                                            <td>
                                                <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                                                    {entry.reporterName}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#999' }}>ID: #{entry.reporterId}</div>
                                            </td>
                                            <td>
                                                <span style={{
                                                    background: '#fff3e0', color: '#e65100',
                                                    padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600'
                                                }}>
                                                    {getReasonLabel(entry.reasonCode)}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ maxWidth: '250px', fontSize: '13px', color: '#555' }} title={entry.description}>
                                                    {entry.description || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No details</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`admin-badge ${entry.status === 'PENDING' ? 'pending' : entry.status === 'REVIEWED' ? 'active' : 'expired'}`}>
                                                    {entry.status}
                                                </span>
                                            </td>
                                            <td>
                                                {entry.actionTaken !== 'NONE' ? (
                                                    <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                                                        {entry.actionTaken}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
                                                {dayjs(entry.createdAt).format("MMM DD, YYYY")}<br />
                                                <small>{dayjs(entry.createdAt).format("HH:mm")}</small>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center p-5" style={{ opacity: 0.5 }}>
                                            No reports found for this target.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportDetails;
